// Maps the specialty text a provider record carries onto schema.org's
// MedicalSpecialty enumeration, for `Physician.medicalSpecialty` (VIT-17746
// follow-up).
//
// Why this exists: the property's range is an enumeration, not text. Shipping the
// endpoint's display string put an invalid value on every provider page —
// validator.schema.org rejected "Internal Medicine, Cardiovascular Disease" with
// INVALID_SCHEMA_ENUM_VALUE. The fix has to be a mapping, because the two
// vocabularies are different sizes: NUCC has ~870 codes and the enumeration has
// 41 members, so this is lossy by construction and only ever approximates.
//
// Keyword rules over the display text rather than a code table: the provider
// endpoint resolves `specialty` to a display string (`Cardiology (Cardiovascular
// Disease)`, `Psychiatry & Neurology`, `Orthopaedic Surgery`), and the NUCC code
// behind it is not on the record by the time atlas-web sees it. Rules also
// degrade gracefully — a taxonomy row nobody has seen before still lands on the
// right member if it names its field in the usual way, where a code table would
// silently emit nothing.

/**
 * The members of https://schema.org/MedicalSpecialty, as published.
 *
 * All 42 of them. Transcribed rather than fetched: a vocabulary, not data, and it
 * moves on schema.org's release cadence rather than ours. Held as a list so the
 * unit test can assert every member named in RULES below is real — the failure
 * this guards against is a plausible invention like `Cardiology` or
 * `Ophthalmology`, neither of which the enumeration contains.
 *
 * Checked against `schemaorg-current-https.jsonld` rather than the HTML page,
 * which is how `Dermatologic` was found missing on the first pass. Note when
 * re-checking that a member's `@type` may be a list: `Radiography` is both a
 * MedicalSpecialty and a MedicalImagingTechnique, so an equality test on `@type`
 * drops it and makes a complete list look one short.
 */
export const MEDICAL_SPECIALTY_MEMBERS = [
  'Anesthesia',
  'Cardiovascular',
  'CommunityHealth',
  'Dentistry',
  // Both are published, and they are not synonyms in the vocabulary: Dermatology
  // is "a specific branch of medical science that pertains to diagnosis and
  // treatment of disorders of skin", Dermatologic is the looser "something
  // relating to or practicing dermatology". The rules below target Dermatology;
  // Dermatologic is listed only because this array claims to be the published
  // set, and a test checks it.
  'Dermatologic',
  'Dermatology',
  'DietNutrition',
  'Emergency',
  'Endocrine',
  'Gastroenterologic',
  'Genetic',
  'Geriatric',
  'Gynecologic',
  'Hematologic',
  'Infectious',
  'LaboratoryScience',
  'Midwifery',
  'Musculoskeletal',
  'Neurologic',
  'Nursing',
  'Obstetric',
  'Oncologic',
  'Optometric',
  'Otolaryngologic',
  'Pathology',
  'Pediatric',
  'PharmacySpecialty',
  'Physiotherapy',
  'PlasticSurgery',
  'Podiatric',
  'PrimaryCare',
  'Psychiatric',
  'PublicHealth',
  'Pulmonary',
  'Radiography',
  'Renal',
  'RespiratoryTherapy',
  'Rheumatologic',
  'SpeechPathology',
  'Surgical',
  'Toxicologic',
  'Urologic',
] as const

export type MedicalSpecialtyMember = (typeof MEDICAL_SPECIALTY_MEMBERS)[number]

/**
 * Ordered match rules. **Order is load-bearing**: a segment takes its FIRST match
 * and stops, so the specific rule has to precede the general one it would
 * otherwise be swallowed by.
 *
 * The pairs that force the ordering, each a real NUCC display string:
 *   - `Speech-Language Pathology` before `Pathology` — both contain "patholog".
 *   - `Respiratory Therapy` before `Pulmonary`.
 *   - `Vascular Neurology` — the neuro rule precedes `/cardio|vascular/`, so it
 *     lands on Neurologic. `Cardiovascular Disease` matches no neuro rule and
 *     still reaches Cardiovascular.
 *   - `Plastic Surgery`, `Orthopaedic Surgery` and `Dentist, Oral and
 *     Maxillofacial Surgery` before the bare `/surg/` fallback, which exists
 *     only for rows whose field nothing else recognises.
 *   - `Midwifery` before `Nursing` — "Certified Nurse Midwife" is both, and the
 *     midwifery member is the more specific claim.
 *
 * `\b` anchors where a substring lurks inside an unrelated word: `/\burolog/`
 * must not fire on "**Neur**ology", and `/\bpatholog/` keeps "Speech-Language
 * Pathology" from reaching the generic rule if the ordering above ever changes.
 *
 * Several rules are narrower than the obvious keyword, because the obvious
 * keyword claimed fields the provider does not hold (VIT-17992 review). The
 * recurring mistake in every case below was writing the rule from what a member
 * *means* rather than from the NUCC strings that actually reach it — the broad
 * keyword never earned its place, and always cost a row that named its field
 * plainly:
 *   - The neuro rule names neuro *fields* (`neurolog`, `neurosurg`, …) rather
 *     than the bare `neuro` prefix. `Otolaryngology, Otology & Neurotology` and
 *     `Radiology, Neuroradiology` are ENT and radiology subspecialties that
 *     merely start with "neuro"; under the prefix they each also claimed
 *     Neurologic, and now they fall through to their own rule instead.
 *   - Pulmonary names only pulmonology. `thoracic` and `critical care` used to
 *     reach it, which made `Thoracic Surgery` and `Surgery, Surgical Critical
 *     Care` Pulmonary when both are surgical fields. `Critical Care Medicine`
 *     on its own now maps to nothing, which is the honest answer: critical care
 *     cuts across anaesthesia, surgery, paediatrics and internal medicine, and
 *     the enumeration has no member for it.
 *   - PlasticSurgery names only `plastic`. It also matched `reconstructive` and
 *     `maxillofacial`, and neither earned its place: `reconstructive` claimed
 *     `Orthopaedic Surgery, Adult Reconstructive Orthopaedic Surgery`, which is
 *     joint replacement, while adding nothing (every NUCC row that is plastic
 *     surgery says so). `maxillofacial` appears only on the three `Dentist,
 *     Oral and Maxillofacial …` rows, so it moved to the Dentistry rule, where
 *     it decides `… Maxillofacial Surgery`; the Pathology and Radiology rows
 *     reach their own rule first, which is the right answer for both.
 *   - Psychiatric names only `psychiat`. `mental health`, `behavioral health`
 *     and `addiction` also reached it, which put a psychiatrist's field on
 *     `Counselor, Mental Health`, `Counselor, Addiction (Substance Use
 *     Disorder)` and `Clinical Nurse Specialist, Psych/Mental Health`. A
 *     counsellor is licensed to do a materially different job from a
 *     psychiatrist — no prescribing — so this is the same refusal as the
 *     ophthalmology gap below. It also stops `Behavioral Health & Social
 *     Service Providers`, a NUCC grouping, from resolving at all, which is what
 *     already happens to `Allopathic & Osteopathic Physicians`. Nothing is lost:
 *     `psychiatry` still catches every psychiatry row, `Addiction Psychiatry`
 *     included, and `Internal Medicine, Addiction Medicine` keeps PrimaryCare.
 *     Narrowed a second time from `psychiat` to `psychiatry|psychiatrist`, on a
 *     lexical convention that holds throughout NPPES: the physician rows use the
 *     noun (`Psychiatry & Neurology, Psychiatry`, `… Addiction Psychiatry`,
 *     `… Geriatric Psychiatry`) while the adjective marks rows held by someone
 *     else — `Pharmacist, Psychiatric` (1835P1300X), `Psychiatric Hospital`
 *     (283Q00000X), `Psychiatric Unit` (273R00000X). Matching the noun keeps
 *     every psychiatrist and drops all three of those.
 *   - Musculoskeletal no longer lists `podiatric surgery`, which sat ahead of
 *     the Podiatric rule and beat it. It bought nothing — `podiatr` already
 *     matches, and Podiatric is the more specific member of the two.
 *
 * Two more went the same way, found by applying that test to the rest of the
 * table rather than waiting for another report: `rehabilitat` on Physiotherapy
 * (`Physical Medicine & Rehabilitation` already matches on `physical medicine`,
 * so it only ever decided `Counselor, Rehabilitation` and `Registered Nurse,
 * Rehabilitation` — wrongly) and `radiation therapy` on Oncologic (`Radiology,
 * Radiation Oncology` already matches on `oncolog`, so it only ever decided
 * `Radiologic Technologist, Radiation Therapy`, which is a technologist).
 *
 * Two more of the same kind, and the distinction that decides them:
 *   - `neuromuscul` narrowed to `neuromuscular`. It was written for neurology's
 *     `Neuromuscular Medicine` but also caught osteopathic `Neuromusculoskeletal
 *     Medicine` (NMM/OMM), a different specialty. NMM now resolves through the
 *     Musculoskeletal rule instead of going unmapped, because that member is
 *     defined as "diagnosis and treatment of disorders of muscles, ligaments and
 *     skeletal system" — which is NMM's scope exactly.
 *   - `optician` and `opticianry` dropped from Optometric, which the vocabulary
 *     defines as "testing visual acuity and prescribing corrective lenses". An
 *     optician dispenses and fits; they do not prescribe. Same refusal as the
 *     ophthalmology gap below, in the other direction.
 *
 * Note what separates those two, because it is the rule for the next one: a
 * member naming a *body system* (Musculoskeletal, Cardiovascular, Renal) is safe
 * to apply to any provider who works on it, while a member naming a *practice or
 * profession* (Optometric, Psychiatric, Midwifery) asserts a licence and must
 * only go to someone who holds it. The vocabulary's own wording is the tell —
 * compare Optometric, "the science or practice of testing visual acuity and
 * prescribing corrective lenses", against Emergency, "a specific branch of
 * medical science that deals with … trauma or sudden illness". The first names
 * what someone is licensed to do; the second names a field, and anyone
 * practising in it may claim it.
 *
 * Two more profession members that were reaching people who don't hold them:
 *   - `pharmac` narrowed to `pharmacist|pharmacy`. PharmacySpecialty is "the
 *     practice or art and science of preparing and dispensing drugs and
 *     medicines", and the bare stem also caught `Clinical Pharmacology` — a
 *     physician subspecialty whose holders don't dispense. `pharmacology` has no
 *     `pharmacy` in it, so the narrower rule separates them and that row maps to
 *     nothing, there being no member for it.
 *   - `nursing` given a `(?! home)` guard. Nursing is "a health profession of a
 *     person formally educated and trained in the care of the sick or infirm",
 *     which `Nursing Home Administrator` — facility management — is not.
 *
 * `emergency` narrowed to `emergency medicine|emergency medical` on that basis.
 * Emergency is a field, so an EMT — whose whole job is the initial treatment of
 * trauma and sudden illness — does hold it, and `Emergency Medical Technician,
 * Basic` keeps the member. What the bare keyword also caught was
 * `Personal Emergency Response Attendant`, a non-clinical alert-system role that
 * practises no branch of medicine at all, and that row now maps to nothing.
 *
 * `diabet` on Endocrine is the one broad keyword left standing. It decides only
 * the `… Diabetes Educator` rows, and it stays for exactly that reason: Endocrine
 * names a body system, so it describes what those providers work on without
 * claiming a credential they lack.
 */
const RULES: readonly (readonly [RegExp, MedicalSpecialtyMember])[] = [
  [/speech|language patholog/, 'SpeechPathology'],
  [/respiratory therap/, 'RespiratoryTherapy'],
  [/physical therap|physiotherap|physical medicine/, 'Physiotherapy'],
  [/plastic/, 'PlasticSurgery'],
  [/orthopaed|orthoped|sports medicine|hand surgery|neuromusculoskeletal|osteopathic manipulative/, 'Musculoskeletal'],
  [/neurolog|neurosurg|neurophysiolog|neurodevelopment|neuromuscular|neurocritical/, 'Neurologic'],
  [/cardio|cardiac|vascular/, 'Cardiovascular'],
  [/psychiatry|psychiatrist/, 'Psychiatric'],
  [/derma/, 'Dermatology'],
  [/gastro|hepatolog/, 'Gastroenterologic'],
  [/\burolog/, 'Urologic'],
  [/obstetric/, 'Obstetric'],
  [/gyneco|gynaeco/, 'Gynecologic'],
  [/oncolog/, 'Oncologic'],
  [/h[ae]matolog/, 'Hematologic'],
  [/endocrin|diabet/, 'Endocrine'],
  [/infectious/, 'Infectious'],
  [/pulmonar|pulmonolog/, 'Pulmonary'],
  [/nephrolog|renal|dialysis/, 'Renal'],
  [/rheumat/, 'Rheumatologic'],
  [/an[ae]sthesi/, 'Anesthesia'],
  [/emergency medicine|emergency medical/, 'Emergency'],
  [/otolaryngolog|otology|rhinolog|laryngolog/, 'Otolaryngologic'],
  [/radiolog|imaging|nuclear medicine|sonograph/, 'Radiography'],
  [/laborator/, 'LaboratoryScience'],
  [/\bpatholog/, 'Pathology'],
  [/optometr/, 'Optometric'],
  [/podiatr/, 'Podiatric'],
  [/dentist|dental|orthodont|periodont|endodont|prosthodont|oral surgery|maxillofacial/, 'Dentistry'],
  [/midwif/, 'Midwifery'],
  [/nurse|nursing(?! home)/, 'Nursing'],
  [/pharmacist|pharmacy/, 'PharmacySpecialty'],
  [/nutrition|dietet|dietitian|dietician/, 'DietNutrition'],
  [/genetic/, 'Genetic'],
  [/toxicolog/, 'Toxicologic'],
  [/public health|preventive medicine/, 'PublicHealth'],
  [/community health/, 'CommunityHealth'],
  [/family medicine|family practice|general practice|internal medicine|primary care|general medicine/, 'PrimaryCare'],
  // Last resort, and only for a segment nothing above recognised: it says the
  // modality without saying the field, which is still true and still useful.
  [/surger|surgical/, 'Surgical'],
]

/**
 * The two members that name a patient population rather than a field, held apart
 * from RULES because they must NOT consume the segment they match.
 *
 * Every other member answers "which body system or modality"; these two answer
 * "which patients", and a NUCC subspecialty row routinely says both in one
 * breath — `Pediatrics, Pediatric Endocrinology` is a claim about children AND
 * about the endocrine system. `medicalSpecialty` is repeatable, so the honest
 * markup is both members.
 *
 * Under a single first-match table it could only ever be one of them, and which
 * one fell out of accidental rule order: `Pediatrics, Pediatric Cardiology` kept
 * both because `cardio` happened to sit above `pediatr`, while `Pediatrics,
 * Pediatric Endocrinology` silently dropped Endocrine because `endocrin` sat
 * below it — along with Hematologic, Oncologic, Pulmonary, Renal, Rheumatologic
 * and Infectious on their equivalents (VIT-17992 review).
 *
 * Matching these first and then continuing into RULES is what makes the
 * segment-splitting consistent. It deliberately does not generalise to matching
 * every rule per segment: the ordering traps documented above exist precisely
 * because a segment can contain a keyword belonging to a field it does not hold
 * — `Vascular Neurology` would pick up Cardiovascular, `Speech-Language
 * Pathology` would pick up Pathology. Only a population modifier is safe to add
 * alongside a field, because it contradicts nothing.
 */
const POPULATION_RULES: readonly (readonly [RegExp, MedicalSpecialtyMember])[] = [
  [/pediatr|paediatr|neonat/, 'Pediatric'],
  [/geriatr|gerontolog/, 'Geriatric'],
]

/**
 * Split a display string into the fields it names.
 *
 * NUCC packs several into one row — `Psychiatry & Neurology`, `Internal Medicine,
 * Cardiovascular Disease`, `Cardiology (Cardiovascular Disease)` — and each half
 * is a separate claim. Matching per segment rather than over the whole string is
 * what stops one field's keyword from being shadowed by another's earlier rule.
 */
const segments = (specialty: string): string[] =>
  specialty
    .toLowerCase()
    .split(/[,&/();:]|\band\b|\bwith\b/)
    .map((segment) => segment.trim())
    .filter((segment) => segment !== '')

/**
 * The MedicalSpecialty members a provider's specialty text names, in the order
 * the text names them. Empty when nothing maps.
 *
 * An empty result is a normal outcome, not a fault, and the caller must omit the
 * property rather than fall back to the raw text: absent is valid markup, wrong
 * is not. Two ways to land there —
 *   - The field has no member. `Ophthalmology` is the one that matters: the
 *     enumeration offers `Optometric`, and an ophthalmologist is a surgeon while
 *     an optometrist is not, so mapping one to the other would misstate a
 *     regulated credential. Better to say nothing.
 *   - The text names no field at all (`Allopathic & Osteopathic Physicians`,
 *     which is a NUCC grouping rather than a specialty).
 */
export const medicalSpecialtiesFor = (specialty: string | null | undefined): MedicalSpecialtyMember[] => {
  if (specialty == null) return []

  const found: MedicalSpecialtyMember[] = []
  // Deduped: `Cardiology (Cardiovascular Disease)` names one field twice, and
  // repeating the member would assert it twice.
  const claim = (member: MedicalSpecialtyMember): void => {
    if (!found.includes(member)) found.push(member)
  }

  for (const segment of segments(specialty)) {
    // Population before field, and additively — see POPULATION_RULES. A segment
    // naming both ("pediatric endocrinology") is two claims, not a contest.
    for (const [pattern, member] of POPULATION_RULES) {
      if (pattern.test(segment)) claim(member)
    }

    const rule = RULES.find(([pattern]) => pattern.test(segment))
    if (rule != null) claim(rule[1])
  }

  return found
}

/** The enumeration member as the absolute URL JSON-LD refers to it by. */
export const medicalSpecialtyUrl = (member: MedicalSpecialtyMember): string => `https://schema.org/${member}`
